/* eslint-disable react-hooks/exhaustive-deps */
import { useNavigate } from "react-router-dom";

import { FieldArray, Form, Formik } from "formik";
import * as Yup from "yup";

import Stack from "@mui/material/Stack";
import LoadingButton from "@mui/lab/LoadingButton";
import {
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Typography,
  TextField,
  useMediaQuery,
  Divider,
} from "@mui/material";
import { Card, CardContent, CardHeader } from "@mui/material";
import React, { useEffect, useState } from "react";
import { Cancel, Add } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import JumboAutoComplete from "@jumbo/components/JumboAutoComplete";
import { useSnackbar } from "notistack";
import { PRODUCTS_INWARD } from "app/routes/constant";
import {
  createProductInward,
  getProductList,
  getProductProcessLots,
} from "app/redux/slice/productSlice";
import { getVariantList } from "app/redux/slice/variantSlice";

const GODOWN_OPTIONS = [
  { id: 1, name: "Vipulbhai" },
  { id: 2, name: "Dharmendra" },
  { id: 3, name: "Fenil" },
];

// Empty variant template
const emptyVariant = () => ({
  variantId: null,
  godownType: null,
  millNo: "",
  lotNo: null,
  quantity: 0,
});

// Empty product template
const emptyProduct = () => ({
  productId: null,
  gp: "",
  variants: [emptyVariant()],
});

/**
 * Distribute totalQty across processes sorted by processNo (ascending).
 * Returns array of { processId, processNo, qty }
 */
const distributeQtyToProcesses = (processes, totalQty) => {
  // Sort by processNo ascending
  const sorted = [...processes].sort((a, b) => a.processNo - b.processNo);
  const result = [];
  let remaining = totalQty;

  for (const p of sorted) {
    if (remaining <= 0) break;
    if (p.pendingQty <= 0) continue;
    const fill = Math.min(remaining, p.pendingQty);
    result.push({ processId: p.processId, processNo: p.processNo, qty: fill });
    remaining -= fill;
  }

  return result;
};

const ProductInwardAdd = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const productData = useSelector((state) => state?.product);
  const variantData = useSelector((state) => state?.variant);

  const mobileView = useMediaQuery("(max-width:575px)");

  // Store lotList per product-row + variant-row: key = `${productIndex}-${variantIndex}`
  const [lotListByRow, setLotListByRow] = useState({});

  const rowKey = (pi, vi) => `${pi}-${vi}`;

  const validationSchema = Yup.object({
    products: Yup.array().of(
      Yup.object({
        productId: Yup.object().nullable().required("The product is required"),
        variants: Yup.array().of(
          Yup.object({
            variantId: Yup.object()
              .nullable()
              .required("The variant is required"),
            lotNo: Yup.object().nullable().required("Please select lot number"),
            quantity: Yup.number()
              .nullable()
              .typeError("Quantity must be a number")
              .required("Please enter quantity")
              .min(1, "Quantity cannot be 0 or negative")
              .integer("Quantity must be a whole number")
              .test("max-pending-qty", function (value) {
                const { lotNo } = this.parent;
                if (!lotNo || !value) return true;
                if (value <= (lotNo?.pendingQty || 0)) return true;
                return this.createError({
                  message: `Quantity cannot exceed pending quantity ${
                    lotNo?.pendingQty || 0
                  }`,
                });
              }),
          }),
        ),
      }),
    ),
  });

  useEffect(() => {
    dispatch(getProductList({ sort: 1, field: "name" }));
    dispatch(getVariantList({ field: "name", sort: 1 }));
  }, []);

  return (
    <>
      <Card>
        <CardHeader title={`Add Inward Product`} />
        <CardContent>
          <Formik
            initialValues={{
              products: [emptyProduct()],
            }}
            validationSchema={validationSchema}
            validate={(values) => {
              const errors = { products: [] };
              let hasDuplicate = false;

              values.products.forEach((item, pi) => {
                errors.products[pi] = { variants: [] };
                item.variants?.forEach((v, vi) => {
                  errors.products[pi].variants[vi] = {};
                  if (!item.productId || !v.variantId) return;

                  const isDuplicate = values.products.some((otherProd, opi) =>
                    otherProd.variants?.some((otherV, ovi) => {
                      if (opi === pi && ovi === vi) return false;
                      return (
                        otherProd?.productId?._id === item?.productId?._id &&
                        otherV?.variantId?._id === v?.variantId?._id
                      );
                    }),
                  );

                  if (isDuplicate) {
                    errors.products[pi].variants[vi].variantId =
                      "This Product & Variant combination is already added";
                    hasDuplicate = true;
                  }
                });
              });

              return hasDuplicate ? errors : {};
            }}
            enableReinitialize={true}
            validateOnChange={false}
            onSubmit={async (data, { setSubmitting }) => {
              const productArr = [];
              const globalGP = data.products?.[0]?.gp || "";

              data.products?.forEach((prod, pi) => {
                prod.variants?.forEach((v, vi) => {
                  const key = rowKey(pi, vi);
                  const totalQty = Number(v?.quantity || 0);

                  const selectedLotData = (lotListByRow[key] || []).find(
                    (lot) => lot.lotNo === v?.lotNo?.lotNo,
                  );

                  if (!selectedLotData || totalQty <= 0) return;

                  const commonFields = {
                    productId: prod?.productId?._id,
                    variantId: v?.variantId?._id,
                    godownType: v?.godownType?.name,
                    millNo: v?.millNo || "",
                    gp: globalGP,
                    lotNo: v?.lotNo?.lotNo?.trim(),
                  };

                  // Distribute qty across processes (ascending processNo order)
                  const distributed = distributeQtyToProcesses(
                    selectedLotData.processIds || [],
                    totalQty,
                  );

                  distributed.forEach(({ processId, qty }) => {
                    productArr.push({
                      ...commonFields,
                      processId,
                      quantity: qty,
                    });
                  });
                });
              });

              setSubmitting(true);
              const response = await dispatch(
                createProductInward({ items: productArr }),
              ).unwrap();
              if (response.status === 200) {
                enqueueSnackbar(response.data?.responseMessage, {
                  variant: "success",
                });
                navigate(PRODUCTS_INWARD);
                setSubmitting(false);
              } else {
                enqueueSnackbar(response.data?.responseMessage, {
                  variant: "error",
                });
              }
            }}
          >
            {({ values, errors, isSubmitting, setFieldValue }) => {
              // GP from first product row
              const globalGP = values.products?.[0]?.gp || "";

              return (
                <Form>
                  <FieldArray name="products">
                    {({ remove: removeProduct, push: pushProduct }) => (
                      <div>
                        {values.products.map((prod, pi) => (
                          <Card
                            key={pi}
                            variant="outlined"
                            sx={{ mb: 2, p: 2, position: "relative" }}
                          >
                            {/* Remove product button */}
                            {pi !== 0 && (
                              <IconButton
                                size="small"
                                sx={{ position: "absolute", top: 8, right: 8 }}
                                onClick={() => removeProduct(pi)}
                              >
                                <Cancel />
                              </IconButton>
                            )}

                            <Typography
                              variant="subtitle1"
                              fontWeight="bold"
                              mb={2}
                            >
                              Product {pi + 1}
                            </Typography>

                            <Grid container spacing={3} mb={2}>
                              {/* Product Select */}
                              <Grid item xs={12} sm={6}>
                                <JumboAutoComplete
                                  options={productData.list ?? []}
                                  label="Product*"
                                  size="large"
                                  getOptionLabel={(option) => option?.name}
                                  name={`products[${pi}].productId`}
                                  value={prod?.productId}
                                  onChange={(evt, value) => {
                                    setFieldValue(
                                      `products[${pi}].productId`,
                                      value,
                                    );
                                    // Reset all variants when product changes
                                    setFieldValue(`products[${pi}].variants`, [
                                      emptyVariant(),
                                    ]);
                                  }}
                                  helperText={
                                    errors?.products?.length &&
                                    errors?.products[pi]?.productId
                                  }
                                />
                              </Grid>

                              {/* GP — only show on first product row; rest auto-filled */}
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  sx={{ width: "100%" }}
                                  name={`products[0].gp`}
                                  label="GP (same for all)"
                                  size="large"
                                  value={pi === 0 ? prod?.gp : globalGP}
                                  disabled={pi !== 0}
                                  onChange={(e) => {
                                    setFieldValue(
                                      `products[0].gp`,
                                      e.target.value,
                                    );
                                  }}
                                  helperText={
                                    pi === 0
                                      ? "This GP will apply to all product rows"
                                      : `Auto-filled: ${globalGP}`
                                  }
                                />
                              </Grid>
                            </Grid>

                            <Divider sx={{ mb: 2 }} />

                            {/* ---- VARIANTS NESTED ARRAY ---- */}
                            <FieldArray name={`products[${pi}].variants`}>
                              {({
                                remove: removeVariant,
                                push: pushVariant,
                              }) => (
                                <div>
                                  {prod.variants?.map((v, vi) => {
                                    const key = rowKey(pi, vi);

                                    // Get total pendingQty for selected lot
                                    const selectedLotData = (
                                      lotListByRow[key] || []
                                    ).find(
                                      (lot) => lot.lotNo === v?.lotNo?.lotNo,
                                    );
                                    const maxQty =
                                      selectedLotData?.processIds?.reduce(
                                        (sum, p) => sum + (p.pendingQty || 0),
                                        0,
                                      ) ??
                                      v?.lotNo?.pendingQty ??
                                      0;

                                    return (
                                      <div key={vi}>
                                        {vi > 0 && (
                                          <Divider
                                            sx={{
                                              my: 2,
                                              borderStyle: "dashed",
                                            }}
                                          />
                                        )}

                                        <Grid
                                          container
                                          spacing={2}
                                          mb={2}
                                          alignItems="center"
                                        >
                                          {/* Variant label */}
                                          <Grid item xs={12}>
                                            <Stack
                                              direction="row"
                                              justifyContent="space-between"
                                              alignItems="center"
                                            >
                                              <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                fontWeight="bold"
                                              >
                                                Variant {vi + 1}
                                              </Typography>
                                              {vi !== 0 && (
                                                <IconButton
                                                  size="small"
                                                  onClick={() => {
                                                    removeVariant(vi);
                                                  }}
                                                >
                                                  <Cancel fontSize="small" />
                                                </IconButton>
                                              )}
                                            </Stack>
                                          </Grid>

                                          {/* Variant Select */}
                                          <Grid item xs={12} sm={2.5}>
                                            <JumboAutoComplete
                                              options={
                                                prod?.productId
                                                  ? variantData?.list.filter(
                                                      (l) =>
                                                        l?.product?._id ===
                                                        prod?.productId?._id,
                                                    ) || []
                                                  : []
                                              }
                                              label="Variant*"
                                              size="large"
                                              getOptionLabel={(option) =>
                                                option?.name
                                              }
                                              name={`products[${pi}].variants[${vi}].variantId`}
                                              value={v?.variantId}
                                              onChange={(evt, value) => {
                                                setFieldValue(
                                                  `products[${pi}].variants[${vi}].variantId`,
                                                  value,
                                                );
                                                setFieldValue(
                                                  `products[${pi}].variants[${vi}].lotNo`,
                                                  null,
                                                );
                                                setFieldValue(
                                                  `products[${pi}].variants[${vi}].quantity`,
                                                  0,
                                                );
                                                if (value?._id) {
                                                  dispatch(
                                                    getProductProcessLots(
                                                      value._id,
                                                    ),
                                                  ).then((response) => {
                                                    if (
                                                      response?.payload?.data
                                                        ?.data?.lotList
                                                    ) {
                                                      setLotListByRow(
                                                        (prev) => ({
                                                          ...prev,
                                                          [key]:
                                                            response.payload
                                                              .data.data
                                                              .lotList,
                                                        }),
                                                      );
                                                    }
                                                  });
                                                } else {
                                                  setLotListByRow((prev) => ({
                                                    ...prev,
                                                    [key]: [],
                                                  }));
                                                }
                                              }}
                                              helperText={
                                                errors?.products?.[pi]
                                                  ?.variants?.[vi]?.variantId
                                              }
                                            />
                                          </Grid>

                                          {/* Lot No */}
                                          <Grid item xs={12} sm={2.75}>
                                            <JumboAutoComplete
                                              options={lotListByRow[key] || []}
                                              label="Lot No*"
                                              size="large"
                                              getOptionLabel={(option) =>
                                                `${option?.lotNo} (Pending: ${option?.pendingQty})`
                                              }
                                              name={`products[${pi}].variants[${vi}].lotNo`}
                                              value={v?.lotNo}
                                              onChange={(evt, value) => {
                                                setFieldValue(
                                                  `products[${pi}].variants[${vi}].lotNo`,
                                                  value,
                                                );
                                                setFieldValue(
                                                  `products[${pi}].variants[${vi}].quantity`,
                                                  0,
                                                );
                                                // Auto-fill millNo from selected lot's millName
                                                setFieldValue(
                                                  `products[${pi}].variants[${vi}].millNo`,
                                                  value?.millName || "",
                                                );
                                              }}
                                              helperText={
                                                errors?.products?.[pi]
                                                  ?.variants?.[vi]?.lotNo
                                              }
                                              disabled={!v?.variantId}
                                            />
                                          </Grid>

                                          {/* Mill No */}
                                          <Grid item xs={12} sm={2.5}>
                                            <TextField
                                              sx={{ width: "100%" }}
                                              label="Mill No"
                                              size="large"
                                              onChange={(e) =>
                                                setFieldValue(
                                                  `products[${pi}].variants[${vi}].millNo`,
                                                  e.target.value,
                                                )
                                              }
                                              value={v?.millNo}
                                            />
                                          </Grid>

                                          {/* Godown */}
                                          <Grid item xs={12} sm={2.5}>
                                            <JumboAutoComplete
                                              options={GODOWN_OPTIONS}
                                              label="Godown Type"
                                              size="large"
                                              getOptionLabel={(option) =>
                                                option?.name
                                              }
                                              name={`products[${pi}].variants[${vi}].godownType`}
                                              value={v?.godownType}
                                              onChange={(evt, value) =>
                                                setFieldValue(
                                                  `products[${pi}].variants[${vi}].godownType`,
                                                  value,
                                                )
                                              }
                                              helperText={
                                                errors?.products?.[pi]
                                                  ?.variants?.[vi]?.godownType
                                              }
                                            />
                                          </Grid>

                                          {/* Qty — now EDITABLE, max = total pendingQty of all processes */}
                                          <Grid item xs={12} sm={1.75}>
                                            <TextField
                                              sx={{ width: "100%" }}
                                              label="Qty*"
                                              type="number"
                                              size="large"
                                              disabled={!v?.lotNo}
                                              value={v?.quantity || ""}
                                              onWheel={(e) => e.target.blur()} // ✅ scroll disable
                                              onChange={(e) => {
                                                const inputVal = e.target.value;
                                                if (inputVal === "") {
                                                  setFieldValue(
                                                    `products[${pi}].variants[${vi}].quantity`,
                                                    "",
                                                  );
                                                  return;
                                                }
                                                const val = Math.max(
                                                  0,
                                                  Math.min(
                                                    Math.floor(
                                                      Number(inputVal),
                                                    ),
                                                    maxQty,
                                                  ),
                                                );
                                                setFieldValue(
                                                  `products[${pi}].variants[${vi}].quantity`,
                                                  val,
                                                );
                                              }}
                                              inputProps={{
                                                min: 0,
                                                max: maxQty,
                                              }}
                                              error={
                                                !!errors?.products?.[pi]
                                                  ?.variants?.[vi]?.quantity
                                              }
                                              helperText={
                                                errors?.products?.[pi]
                                                  ?.variants?.[vi]?.quantity ||
                                                (v?.lotNo
                                                  ? `Max: ${maxQty}`
                                                  : "")
                                              }
                                            />
                                          </Grid>
                                        </Grid>
                                      </div>
                                    );
                                  })}

                                  {/* Add Variant Button */}
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<Add />}
                                    sx={{ mt: 1 }}
                                    disabled={!prod?.productId}
                                    onClick={() => pushVariant(emptyVariant())}
                                  >
                                    Add Variant
                                  </Button>
                                </div>
                              )}
                            </FieldArray>
                          </Card>
                        ))}

                        {/* Add Product Button */}
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<Add />}
                          onClick={() => pushProduct(emptyProduct())}
                        >
                          Add Product
                        </Button>
                      </div>
                    )}
                  </FieldArray>

                  <br />

                  <Stack
                    spacing={{ xs: 1, sm: 2 }}
                    direction="row"
                    justifyContent="flex-end"
                    alignItems="flex-end"
                    sx={{ width: "100%", mt: 1 }}
                  >
                    <LoadingButton
                      variant="outlined"
                      color="secondary"
                      size="large"
                      onClick={() => navigate(PRODUCTS_INWARD)}
                      fullWidth={mobileView}
                    >
                      Cancel
                    </LoadingButton>
                    <LoadingButton
                      type="submit"
                      variant="contained"
                      size="large"
                      loading={isSubmitting}
                      loadingIndicator={<CircularProgress size={20} />}
                      fullWidth={mobileView}
                    >
                      Save
                    </LoadingButton>
                  </Stack>
                </Form>
              );
            }}
          </Formik>
        </CardContent>
      </Card>
    </>
  );
};

export default ProductInwardAdd;
